import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';
import {
  CambioConfig,
  ConfigSeguimiento,
  FotoFuga,
  Gestion,
  RespuestaSemana,
} from 'src/app/shared/services/seguimiento.service';

/** Un whale que cayó en rojo esta semana. */
export interface AvisoAlerta {
  semanaId: number;
  clientes: Array<{ clienteId: number; usdt: number; baseUsdt: number }>;
}

export interface AvisoCliente {
  clienteId: number;
  gestion: Gestion;
  evento?: unknown;
}

export interface AvisoConfig {
  config: ConfigSeguimiento;
  aplicados: CambioConfig[];
  por: string | null;
}

/**
 * El canal en vivo del tablero de retención.
 *
 * POR QUÉ EXISTE. Sin esto la pantalla tiene dos opciones y las dos son malas:
 * mostrar lo que había cuando la abrieron —en la reunión de los lunes, justo lo
 * que no sirve— o preguntarle al servidor cada pocos segundos, que con diez
 * operadores mirando es la misma agregación repetida cien veces. Acá el
 * servidor avisa una sola vez para todos.
 *
 * EL TABLERO NO DEPENDE DEL CANAL. La pantalla carga por HTTP al abrir y el
 * canal sólo trae las actualizaciones: si el socket no conecta, se ve el
 * tablero igual, con el «datos al…» de la última foto. Un canal caído no puede
 * dejar la pantalla en blanco.
 *
 * Sigue el patrón de `N58SocketService`: token de Cognito en el saludo y, al
 * reconectar, el token nuevo — que es lo que evita que una sesión renovada deje
 * el canal muerto.
 */
@Injectable({ providedIn: 'root' })
export class SeguimientoSocketService implements OnDestroy {
  private socket: Socket | null = null;

  private conectado$ = new BehaviorSubject<boolean>(false);
  private semana$ = new Subject<RespuestaSemana>();
  private cliente$ = new Subject<AvisoCliente>();
  private fuga$ = new Subject<FotoFuga>();
  private alerta$ = new Subject<AvisoAlerta>();
  private config$ = new Subject<AvisoConfig>();

  constructor(private cognito: CognitoService) {}

  async conectar(): Promise<void> {
    if (this.socket?.connected) return;

    const session = await this.cognito.getSession();
    const token = session?.idToken?.jwtToken;
    if (!token) return;

    const baseUrl = environment.apiNest.replace(/\/+$/, '');
    this.socket = io(`${baseUrl}/seguimiento`, {
      auth: { token },
      transports: ['polling'],
    });

    this.socket.on('connect', () => this.conectado$.next(true));
    this.socket.on('disconnect', async () => {
      this.conectado$.next(false);
      const nueva = await this.cognito.getSession();
      const tokenNuevo = nueva?.idToken?.jwtToken;
      if (tokenNuevo && this.socket) this.socket.auth = { token: tokenNuevo };
    });

    this.socket.on('seguimiento:semana', (d: RespuestaSemana) => this.semana$.next(d));
    this.socket.on('seguimiento:cliente', (d: AvisoCliente) => this.cliente$.next(d));
    this.socket.on('seguimiento:fuga', (d: FotoFuga) => this.fuga$.next(d));
    this.socket.on('seguimiento:alerta', (d: AvisoAlerta) => this.alerta$.next(d));
    this.socket.on('seguimiento:config', (d: AvisoConfig) => this.config$.next(d));
  }

  desconectar(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.conectado$.next(false);
  }

  estaConectado$(): Observable<boolean> {
    return this.conectado$.asObservable();
  }

  /** La semana en curso se recalculó: llega la cabecera y el detalle completo. */
  alSemana$(): Observable<RespuestaSemana> {
    return this.semana$.asObservable();
  }

  /** Cambió la gestión de un cliente: dueño, motivo, próxima acción, contacto. */
  alCliente$(): Observable<AvisoCliente> {
    return this.cliente$.asObservable();
  }

  /** Se movió la lista de perdidos. */
  alFuga$(): Observable<FotoFuga> {
    return this.fuga$.asObservable();
  }

  /** Un whale cayó en rojo. */
  alAlerta$(): Observable<AvisoAlerta> {
    return this.alerta$.asObservable();
  }

  /** Alguien movió un umbral o una meta. */
  alConfig$(): Observable<AvisoConfig> {
    return this.config$.asObservable();
  }

  ngOnDestroy(): void {
    this.desconectar();
  }
}
